import { useForm } from "@inertiajs/vue3"
import { ref } from "vue"
import { z } from "zod"
import { useZodValidation } from "../useZodValidation"

interface TicketProps {
  id: number
  category: 'full_pass' | 'full_content' | 'digital'
  date: string
}

interface DataProps {
  start: string
  end: string
  description: string
  tickets: TicketProps[]
  order_groups: string[]
}


export function useOrder(data: DataProps) {
  const steps = [
    { step: 1, title: 'Tickets' },
    { step: 2, title: 'Payment method' },
  ]

  const form = useForm({
    user_email: '',
    reason_free_order: '',
    group: '',
    items_attributes: [] as {
      ticket_id: number
      quantity: number
    }[],
  })

  const schemaQuantity = useZodValidation(
    z.object({
      items_attributes: z
        .array(
          z.object({
            ticket_id: z.number(),
            quantity: z.number().min(1),
          }),
        )
        .min(1, {
          message: 'Você precisa selecionar pelo menos um ingresso.',
        }),
    }),
    form,
  )

  const schemaInfos = useZodValidation(
    z.object({
      user_email: z.string().email({
        message: 'Por favor, insira um e-mail válido.',
      }),
      reason_free_order: z.string().optional(),
      group: z.enum([
        'purchased_ticket',
        'speaker',
        'sponsor',
        'summit_access',
        'press',
        'production',
        'exhibitor',
        'courtesy',
        'staff',
        'supporter',
        'guest',
      ], {
        message: 'Por favor, selecione um grupo válido.',
      })
    }),
    form,
  )

  const currentStep = ref(1)

  const tickets = ref(data.tickets)

  function handleTicket(ticket: TicketProps, quantity: number) {
    const existingItem = form.items_attributes.find((item) => item.ticket_id === ticket.id)

    if (existingItem) {
      existingItem.quantity = quantity

      if (existingItem.quantity <= 0) {
        form.items_attributes = form.items_attributes.filter((item) => item.ticket_id !== ticket.id)
      }

      return
    }

    form.items_attributes.push({
      ticket_id: ticket.id,
      quantity: quantity,
    })

    return
  }

  function handleSubmit() {
    form.clearErrors()

    if (currentStep.value === 1) {
      if (!schemaQuantity.validate()) return

      return nextStep()
    }

    if (currentStep.value === 2) {
      if (!schemaInfos.validate()) return

      return form.post('/admin/orders')
    }
  }

  function nextStep() {
    if (currentStep.value < steps.length) {
      currentStep.value++
    }
  }

  function findTicketById(id: number) {
    return tickets.value.find((ticket) => ticket.id === id)
  }

  return {
    form,
    steps,
    currentStep,
    tickets,
    nextStep,
    handleTicket,
    handleSubmit,
    findTicketById,
  }
}